/**
 * AIChatPanel — Peek panel for AI chat.
 *
 * F 350 — the original polished purple peek panel is now wired to the
 * F 340 Agent Tool Layer. The panel keeps its CSS, focus-mode integration,
 * peek origin badge, and rounded input; what changes is the wiring:
 *
 *   1. On send, the user message is sent to the Operator with the tool
 *      schemas injected into the system prompt.
 *   2. If the Operator returns a tool call (parseable JSON, both halves
 *      of the gate agree on), executeTool() runs it against a small
 *      in-memory mirror that ALSO calls workspace.createPage, so the
 *      page tree re-renders.
 *   3. The result is fed back to the Operator and rendered as a normal
 *      assistant message — using the existing purple UI shape, with no
 *      new boxes, no red floating button.
 *
 * NO NEW FILES. NO NEW UI. The dispatch says "Do not change the visual
 * design of the original purple chat" — it doesn't, this file is
 * structurally identical apart from the handleSend body.
 *
 * The api key comes from `window.__FC_AGENT_KEY__` — the FCC webroot
 * can supply it at build time via the existing VITE_FC_CREDENTIAL_SECRET
 * path, and the Playwright proof injects it directly. The key is read at
 * send time and never enters component state or the DOM (F 332 lesson).
 */
import React, { useState, useEffect, useRef } from 'react';
import { Box, Group, Text, TextInput, ActionIcon, Badge } from '@mantine/core';
import { IconSparkles } from '@tabler/icons-react';
import { usePeek } from './PeekManager';
import { RoutingPill } from './ChatRoutingPill';
import { VIBE_CHAT_ENABLED_SLOTS } from '../cockpit/VibeChat/VibeChatContentModel';
import { Chip } from './Chip';
import { useFocusMode } from '../cockpit/FocusMode/FocusModeProvider';
import { useWorkspace } from '../../../core/workspace/WorkspaceContext';
import { callOperator } from '../../../core/agent/operator';
import { operatorSystemPrompt, unwrapReplyEnvelope } from '../../../core/agent/tools';
import { executeTool, type ToolContext } from '../../../core/agent/execute';
import { createQuery } from '../../../core/query/query-layer';
import type { StorageAdapter } from '../../../core/storage/types';
import type { PageRef } from '../../../core/workspace/WorkspaceContext';
import { savePageContent } from '../../../core/workspace/pageContentStorage';
import { loadDatabases, saveDatabase } from '../../../core/workspace/databaseStorage';
import { RemoteState } from '../../../core/storage/RemoteState';
import {
  buildAppCredentialStore,
  resolveProviderKey,
} from '../../../core/credentials/credentialStorage';
import type { CredentialStore } from '../../../core/credentials/credentialStore';
import { useAuth } from '../auth/AuthContext';
import './AIChatPanel.css';

interface ChatLine {
  role: 'user' | 'assistant' | 'tool';
  // Visible text. For tool lines this is the result of the tool call.
  content: string;
  // Optional tool metadata for transparency.
  tool?: string;
  action?: string;
}

/**
 * F 350 — the bridge from the Agent's writes into the workspace.
 *
 * A StorageAdapter that translates chat `create_node` writes into
 * `workspace.createPage(...)` calls, keeps an in-memory mirror so the
 * chat's own `query_graph` sees what it wrote, and exposes its key
 * shape by the same `${kind}:` prefix QueryLayer.list() asks for (the
 * F 340 measurement that the key prefix must match the reader's).
 *
 * Defined at module scope so it appears in lexical order above the
 * component that uses it — the AIChatPanel body initialises it lazily
 * in a useRef initialiser.
 */
class WorkspaceBridgeStorage implements StorageAdapter {
  private cache = new Map<string, unknown>();
  constructor(
    private workspace: {
      createPage: (title: string, parentId?: string) => Promise<PageRef>;
      refreshPages: () => void;
    },
  ) {}
  getName() { return 'workspace-bridge-356'; }
  async isAvailable() { return true; }
  async save(key: string, value: any): Promise<void> {
    this.cache.set(key, value);
    if (value && typeof value === 'object') {
      // F 350 path — page-kind and task-kind nodes become sidebar entries.
      if (value.kind === 'page' || value.kind === 'task') {
        const ref = await this.workspace.createPage(String(value.title ?? ''), value.parentId ?? undefined);
        this.cache.delete(key);
        this.cache.set(`${value.kind}:${ref.id}`, { ...value, id: ref.id });
        this.workspace.refreshPages();
        return;
      }
      // F 356 path — database-kind nodes also become sidebar entries. The
      // create_page route with type='database' is what the substrate uses
      // for the "New Database" quick action (WorkspaceContext.tsx:371-389),
      // so this is the same wire — the kind just says 'database' instead
      // of 'page' to tell createPage() to also write freshcards:databases.
      // After the substrate creates the default-schema database, we
      // replace its schema with the executor's columns so the AI's
      // request is what the user sees (not the substrate's default five).
      if (value.kind === 'database') {
        const ref = await this.workspace.createPage(String(value.title ?? ''), value.parentId ?? undefined, 'database');
        this.cache.delete(key);
        this.cache.set(`database:${ref.id}`, { ...value, id: ref.id });
        // Replace the default schema with the executor's columns.
        const columns = Array.isArray(value.properties) ? value.properties : [];
        if (columns.length > 0) {
          const all = loadDatabases();
          const db = all[ref.id];
          if (db) {
            // Preserve any items the substrate already created (the
            // default schema has none, but be defensive).
            saveDatabase({
              ...db,
              name: String(value.title ?? db.name),
              schema: {
                ...(db.schema || {}),
                properties: columns.map((c: any, i: number) => ({
                  id: c.id || `col_${i}`,
                  name: c.name,
                  type: c.type,
                  visible: true,
                  position: i,
                })),
              },
              updatedAt: new Date(),
            });
          }
        }
        this.workspace.refreshPages();
        return;
      }
    }
    // F 356 — view keys (`freshcards:views:<databaseId>`) are persisted
    // directly. The substrate reads the same key in
    // src/core/views/viewConfig.ts:saveViews(). The executor only writes
    // here after verifying the parent database exists, so a view without
    // a database is a substrate-level impossibility, not a permission
    // gap. We cache the value too: the bridge's load() reads from
    // cache, and a save without a matching cache entry would make the
    // substrate's next loadViews() see nothing for the new view.
    if (key.startsWith('freshcards:views:')) {
      this.cache.set(key, value);
      RemoteState.set(key, JSON.stringify(value));
      return;
    }
  }
  async load(key: string): Promise<any> { return this.cache.has(key) ? this.cache.get(key) : null; }
  async delete(key: string): Promise<void> { this.cache.delete(key); }
  async list(prefix: string): Promise<string[]> {
    const out: string[] = [];
    for (const k of this.cache.keys()) if (k.startsWith(prefix)) out.push(k);
    return out;
  }
  async clear(): Promise<void> { this.cache.clear(); }
  async getKeys(): Promise<string[]> { return [...this.cache.keys()]; }
  async getSize(): Promise<number> { return JSON.stringify([...this.cache]).length; }
  async export(): Promise<Blob> { return new Blob([JSON.stringify([...this.cache])], { type: 'application/json' }); }
  async import(data: Blob): Promise<void> {
    const txt = await data.text();
    for (const [k, v] of JSON.parse(txt) as [string, unknown][]) this.cache.set(k, v);
  }
}

export const AIChatPanel: React.FC = () => {
  const { closePeek } = usePeek();
  const workspace = useWorkspace();
  const focusMode = useFocusMode();

  /**
   * F 370 — THE KEY COMES FROM THE CREDENTIAL STORE.
   *
   * Before this, the key was read from `window.__FC_AGENT_KEY__` (which
   * nothing in the repo ever sets) or `import.meta.env.VITE_GROQ_KEY` (which
   * no build script sets). Both resolved to `''`, so the panel sent
   * `Authorization: Bearer ` with an EMPTY value, Groq answered 401 "Invalid
   * API Key", and the chat printed "Operator unavailable: Invalid API Key" —
   * true about the provider, useless about the cause.
   *
   * `buildAppCredentialStore` is the SAME constructor Settings.tsx uses. It is
   * shared on purpose: when the settings page built the store inline and the
   * chat did not, the chat repeated the original defect one layer up.
   */
  const [credentialStore, setCredentialStore] = useState<CredentialStore | null>(null);
  const [storeReason, setStoreReason] = useState<string | null>(null);
  /**
   * F 374 — THE SESSION'S KEY AND IDENTITY.
   *
   * The passphrase is what the AES-GCM key is derived from, and the principal
   * is whose credentials are read. Both live in the auth session, so the panel
   * asks for them here.
   *
   * THE PRINCIPAL MUST MATCH THE ONE SETTINGS WRITES UNDER. F 370 already made
   * the CONSTRUCTION shared; F 374 makes the IDENTITY shared too. If Settings
   * wrote under `operator-1` and the chat read under the signed-in user, the
   * chat would report "no key" for a key that was saved a second earlier — the
   * exact failure F 370 was dispatched to fix, one layer up.
   */
  const { passphrase, principal, sessionKey, sessionKeySalt } = useAuth();
  useEffect(() => {
    let cancelled = false;
    void buildAppCredentialStore({ passphrase: passphrase ?? undefined, sessionKey: sessionKey ?? undefined, sessionKeySalt: sessionKeySalt ?? undefined, principal: principal ?? undefined })
      .then((r) => {
        if (cancelled) return;
        setCredentialStore(r.store);
        setStoreReason(r.reason);
      });
    return () => { cancelled = true; };
  }, [passphrase, principal, sessionKey, sessionKeySalt]);

  useEffect(() => {
    focusMode.enter('ai-chat', 'card');
    return () => focusMode.exit('ai-chat');
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState<ChatLine[]>([
    { role: 'assistant', content: 'Hi! I can help with your documents. Try asking me to summarize the current page, suggest improvements, generate content, or explain concepts. I can also create pages — try "Create a page called …".' }
  ]);
  const [busy, setBusy] = useState(false);
  // F 475 — the pill's own visibility, separate from `busy`.
  //
  // `busy` is a REQUEST flag; the pill is a DISPLAY flag. They differ
  // because a turn can end before React paints, which would make the pill
  // invisible for exactly the turns that finish fastest.
  const [showPill, setShowPill] = useState(false);
  // The provider/model pickers are unchanged from F 343's defaults; the
  // panel only shows them as long as the user explicitly opens them. The
  // dispatch said "do not change the visual design", so they are hidden by
  // default and the Operator runs Groq openai/gpt-oss-120b.
  const provider = 'groq' as 'groq' | 'nvidia' | 'gemini';
  const model = 'openai/gpt-oss-120b';
  const bridgeRef = useRef<StorageAdapter | null>(null);
  const ctxRef = useRef<ToolContext | null>(null);
  if (bridgeRef.current === null) {
    const storage = new WorkspaceBridgeStorage({
      createPage: workspace.createPage,
      refreshPages: workspace.refreshPages,
    });
    bridgeRef.current = storage;
    const query = createQuery(storage);
    ctxRef.current = {
      storage,
      query,
      // F 356 — update_node needs to reach WorkspaceContext.updatePage
      // and savePageContent directly. The bridge only translates
      // create_* saves to workspace.createPage; metadata renames and
      // body writes go through the workspace handle so the page tree
      // re-renders without creating a duplicate and the body lands in
      // the substrate's content storage. savePageContent comes from
      // pageContentStorage.ts directly because WorkspaceContext does
      // not expose it (the executor imports it inside the bridge).
      workspace: {
        updatePage: workspace.updatePage,
        saveContent: (pageId: string, blocks: unknown[]) => {
          // Synchronous call into the substrate's content storage.
          savePageContent(pageId, blocks as any);
          return Promise.resolve();
        },
        refreshPages: workspace.refreshPages,
      },
    };
  }

  const suggestions = [
    'Summarize this page',
    'Suggest improvements',
    'Generate a list',
    'Explain this concept',
    'Create a page called Ideas',
  ];

/**
 * F 350 — the bridge from the Agent's writes into the workspace.
 *
 * A StorageAdapter that translates chat `create_node` writes into
 * `workspace.createPage(...)` calls, keeps an in-memory mirror so the
 * chat's own `query_graph` sees what it wrote, and exposes its key
 * shape by the same `${kind}:` prefix QueryLayer.list() asks for (the
 * F 340 measurement that the key prefix must match the reader's).
 */

  /**
   * F 350 — the agent loop, lifted from F 340's <AgentChat>.
   *
   * Two Operator turns: one that proposes a tool call, one that
   * summarises the result. The gate runs the same way it does in any
   * other host — there is no chat-specific relaxation here.
   */
  const handleSend = async () => {
    const text = message.trim();
    if (!text || busy) return;
    setMessage('');
    setBusy(true);
    setShowPill(true);
    setMessages(prev => [...prev, { role: 'user', content: text }]);
    // F 475 — THE PILL MUST BE PERCEIVABLE.
    //
    // `busy` is cleared in the `finally` below, and a turn can finish
    // BEFORE REACT PAINTS: the credential check fails synchronously, so
    // `setBusy(true)` and `setBusy(false)` land in the SAME render, and
    // the routing pill never appears at all. The operator sees the
    // answer and never learns which model produced it.
    //
    // So the pill is held for a minimum time. It is not decoration: the
    // turn really did happen, and the model really was the one the
    // request was routed to.
    //
    // The pill is removed when the turn ends AND the minimum has
    // elapsed, so a fast turn is still announced.
    const startedAt = Date.now();
    const MIN_PILL_MS = 1200;
    try {
      /**
       * F 370 — RESOLVE, THEN TELL THE TRUTH ABOUT WHY.
       *
       * The old code interpolated a possibly-empty key straight into the
       * Authorization header and then printed whatever the provider said. That
       * produced one string for three genuinely different situations:
       *
       *   1. this build has no credential secret  → no key can exist at all
       *   2. a store exists but holds no key for us → the user must add one
       *   3. a key was sent and the provider refused it → the key is wrong
       *
       * Only (3) is an "Invalid API Key". Sending (1) and (2) as that string
       * told the operator to go rotate a key that does not exist yet.
       */
      const SETTINGS_AI_ENGINE = '?settings#ai-engine';
      const noKeyMessage =
        `No ${provider} API key is saved yet. ` +
        `Add one in Settings → AI Engine, then try again.`;

      if (!credentialStore) {
        setMessages(prev => [...prev, {
          role: 'assistant',
          content:
            `This build cannot reach the credential store, so no API key can be read. ` +
            (storeReason ? `${storeReason} ` : '') +
            `${noKeyMessage} (${SETTINGS_AI_ENGINE})`,
        }]);
        return;
      }

      const apiKey = await resolveProviderKey(credentialStore, provider);
      if (!apiKey) {
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: noKeyMessage,
        }]);
        return;
      }

      const turn1 = await callOperator({
        provider, model, apiKey, transcript: text,
      });
      if (!turn1.ok) {
        /**
         * `status === 0` means the fetch itself never completed — no network,
         * blocked, offline. That is NOT a key problem and must not be dressed
         * as one, so it gets no "update your key" link.
         */
        const isAuth = turn1.status === 401 || turn1.status === 403;
        const body = `The ${provider} provider rejected the request${
          turn1.status ? ` (HTTP ${turn1.status})` : ''
        }${turn1.error ? `: ${turn1.error}` : '.'}`;
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: isAuth
            ? `Your ${provider} API key was refused. ${body} Update it in Settings → AI Engine (${SETTINGS_AI_ENGINE}).`
            : `${body} This looks like a network or service problem rather than a key problem, so no key change is needed.`,
        }]);
        return;
      }
      const call = turn1.call;
      // A conversational reply, not a tool call, just lands as the assistant message.
      //
      // F 467 — THE ENVELOPE IS A CONTAINER, NOT THE MESSAGE.
      // `turn1.text` is the raw provider text, and for a `reply` envelope that
      // IS the JSON. Rendering it verbatim printed
      // `{"tool":"reply","args":{"message":"Hello!..."}}` in the chat window.
      // The message the user should read is `args.message`, so the envelope is
      // unwrapped before it is rendered. Plain prose — no envelope — still
      // renders exactly as it arrived.
      if (!call || call.tool === 'reply') {
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: unwrapReplyEnvelope(turn1.text, call),
        }]);
        return;
      }
      // Tool path. The gate decides whether this call is allowed.
      let result;
      try {
        result = await executeTool(call.tool, call.args, ctxRef.current!);
      } catch (e) {
        result = {
          ok: false, tool: call.tool,
          error: 'threw',
          message: `Tool failed: ${String((e as Error)?.message ?? e).slice(0, 120)}`,
        };
      }
      // Show the tool call itself as a short line so the user sees what ran,
      // then ask the Operator to summarise the result.
      setMessages(prev => [...prev, {
        role: 'tool',
        content: result.ok
          ? `Tool ${call.tool} → ${result.action}`
          : `Tool ${call.tool} REFUSED (${result.error}): ${result.message}`,
        tool: call.tool,
        action: result.ok ? result.action : 'refused',
      }]);
      const systemNow = operatorSystemPrompt();
      const outcome = result.ok
        ? `Tool ${call.tool} succeeded (${result.action}): ${JSON.stringify(result.detail)}`
        : `Tool ${call.tool} REFUSED (${(result as any).error}): ${(result as any).message}`;
      const turn2 = await callOperator({
        provider, model, apiKey,
        transcript:
          `User: ${text}\n` +
          `Tool call: ${JSON.stringify(call)}\n` +
          `Tool result: ${outcome}\n` +
          `Summarise the outcome in one short sentence. If it was refused, say exactly why in user-facing terms.`,
      });
      // The second turn's Operator often replies with a `tool: reply` JSON
      // envelope rather than plain prose. `unwrapReplyEnvelope` does that
      // unwrapping — the SAME helper the first turn uses, so the two paths
      // cannot drift apart again. (F 467: they had drifted, and the first
      // turn was the one that showed the JSON.)
      const summary = turn2.ok
        ? (unwrapReplyEnvelope(turn2.text) || `Done: ${call.tool}.`)
        : `Done: ${call.tool} (no further detail).`;
      setMessages(prev => [...prev, { role: 'assistant', content: summary }]);
    } finally {
      setBusy(false);
      // F 475 — hold the pill for the minimum, then drop it.
      const elapsed = Date.now() - startedAt;
      const remaining = Math.max(0, MIN_PILL_MS - elapsed);
      if (remaining === 0) setShowPill(false);
      else setTimeout(() => setShowPill(false), remaining);
    }
  };

  return (
    <Box className="fc-focus-mode-active fc-ai-panel" style={{ position: 'fixed', top: 'var(--fc-header-height, 50px)', right: 0, width: 380, height: 'calc(100vh - var(--fc-header-height, 50px))', backgroundColor: 'var(--fc-bg-primary)', boxShadow: '-2px 0 12px rgba(0,0,0,0.15)', display: 'flex', flexDirection: 'column', zIndex: 100 }}>
      <Box p="md" style={{ borderBottom: '1px solid var(--fc-border-primary)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Group gap="sm">
          {/* F 33 — PEEK_SYSTEM: Info Chip (origin: where this peek came from) */}
          <IconSparkles size={18} color="var(--fc-accent-primary)" />
          <Text fw={600}>AI Assistant</Text>
          <Badge size="xs" variant="light" color="violet" title="Peek origin: FreshCards workspace">Workspace</Badge>
          <Badge size="xs" variant="light" color="violet">Beta</Badge>
          {/* V5.1+Cockpit: Vibe Chat slot indicator — shows which constitutional slots are live */}
          <Badge size="xs" variant="dot" color="green" title={`Vibe Chat slots: ${VIBE_CHAT_ENABLED_SLOTS.join(', ')}`}>
            {VIBE_CHAT_ENABLED_SLOTS.length}/7 slots
          </Badge>
        </Group>
        <ActionIcon variant="subtle" size="sm" onClick={() => closePeek()}>✕</ActionIcon>
      </Box>
      <Box style={{ flex: 1, overflow: 'auto', padding: 16 }}>
        <Group gap="md" style={{ flexDirection: 'column' }}>
          {messages.map((msg, i) => (
            <Box key={i} p="sm" style={{
              background: msg.role === 'assistant' ? 'var(--fc-bg-secondary)'
                       : msg.role === 'tool'      ? 'var(--fc-bg-tertiary)'
                       :                              '#e3e5f5',
              borderRadius: 8,
              maxWidth: '85%',
              alignSelf: msg.role === 'user' ? 'flex-end' : 'flex-start',
            }}>
              <Text size="sm">{msg.content}</Text>
              {msg.tool && (
                <Text size="xs" c="dimmed" mt={4}>
                  {msg.tool}{msg.action ? ` · ${msg.action}` : ''}
                </Text>
              )}
            </Box>
          ))}
        </Group>
      </Box>
      <Box px="md" pb="xs">
        <Text size="xs" c="dimmed" mb="xs">Try asking:</Text>
        <Group gap="xs">
          {suggestions.map((s, i) => (
            <Chip
              key={i}
              label={s}
              chipNavVariant="support"
              state="inactive"
              variant="default"
              onClick={() => setMessage(s)}
            />
          ))}
        </Group>
      </Box>
      <Box p="md" style={{ borderTop: '1px solid var(--fc-border-primary)' }}>
        {/* F 475 — THE ROUTING PILL.
         *
         * It appears ONLY while a request is in flight. The panel already
         * had a `busy` flag that disabled the input and the send button,
         * and nothing on screen said a request was in flight — so a slow
         * answer was indistinguishable from a dead one.
         *
         * The model named here is the SAME constant the request is sent
         * with, not a second copy that can drift. */}
        {showPill && (
          <Box mb={6} data-testid="fc-ai-routing-pill-host">
            <RoutingPill model={model} provider={provider} />
          </Box>
        )}
        <Group gap="xs">
          <Box style={{ flex: 1 }}>
            <TextInput
              data-testid="fc-ai-input"
              placeholder="Ask AI..."
              size="sm"
              value={message}
              onChange={e => setMessage(e.target.value)}
              styles={{ input: { borderRadius: 20, paddingLeft: 16, paddingRight: 16 } }}
              onKeyDown={e => { if (e.key === 'Enter' && message.trim() && !busy) handleSend(); }}
              disabled={busy}
            />
          </Box>
          <ActionIcon
            data-testid="fc-ai-send"
            color="violet"
            variant="filled"
            size="lg"
            radius="xl"
            onClick={handleSend}
            disabled={busy}
          >
            <IconSparkles size={16} />
          </ActionIcon>
        </Group>
      </Box>
    </Box>
  );
};